import { isTauri } from '@/tauri/utils';
import * as amplitude from '@amplitude/analytics-browser';
import { useEffect, useRef } from 'react';

/**
 * Standard experiment variant values for binary feature flags.
 */
export enum ExposureVariant {
  Treatment = 'treatment',
  Control = 'control',
}

interface UseManualExposureOptions {
  /**
   * The flag key (e.g., 'email-only-signup')
   */
  flagKey: string;
  /**
   * Whether the feature flag is enabled for this user.
   * Maps to variant: `true` → 'treatment', `false`/`undefined`/`null` → 'control'.
   * Ignored when `variant` is provided directly.
   */
  isEnabled: boolean | undefined | null;
  /**
   * Whether the user can currently see/experience the variant. We don't fire the exposure event without it.
   */
  isVisible: boolean;
  /**
   * Optional experiment key to distinguish between multiple runs of the same experiment
   */
  experimentKey?: string;
  /**
   * Optional raw variant string for multivariate experiments (e.g., 'control', 'banner', 'chips').
   * When provided, this value is sent directly instead of deriving 'treatment'/'control' from `isEnabled`.
   */
  variant?: string;
}

/**
 * Hook to manually track experiment exposure in Amplitude.
 *
 * Use this when you need to track exposure at the point of visibility
 * rather than flag evaluation. Fires a single `$exposure` event when
 * `isVisible` becomes true.
 *
 * @example
 * ```tsx
 * function FeatureModal({ isOpen }: Props) {
 *   const isNewFeature = IS_NEW_FEATURE_ENABLED();
 *
 *   // Shorthand: isEnabled maps true → 'treatment', false → 'control'
 *   useManualExposure({
 *     flagKey: 'new-feature',
 *     isEnabled: isNewFeature,
 *     isVisible: isOpen,
 *   });
 *
 *   if (isNewFeature) {
 *     return <NewFeatureModal />;
 *   }
 *   return <OldFeatureModal />;
 * }
 * ```
 */
export const useManualExposure = ({
  flagKey,
  isEnabled,
  isVisible,
  experimentKey,
  variant: variantOverride,
}: UseManualExposureOptions): void => {
  // Use the raw variant string when provided, otherwise map isEnabled to treatment/control
  const variant =
    variantOverride ??
    (isEnabled === true ? ExposureVariant.Treatment : ExposureVariant.Control);

  const exposureTracked = useRef(false);

  // Reset exposure tracking when flag parameters change
  // This ensures a new exposure is tracked if the variant changes mid-session
  useEffect(() => {
    exposureTracked.current = false;
  }, [flagKey, variant, experimentKey]);

  useEffect(() => {
    if (isVisible && !exposureTracked.current) {
      exposureTracked.current = true;
      amplitude.track('$exposure', {
        flag_key: flagKey,
        variant,
        platform: isTauri() ? 'desktop' : 'web',
        ...(experimentKey && { experiment_key: experimentKey }),
      });
    }
  }, [isVisible, flagKey, variant, experimentKey]);
};
